import { format, formatDistanceToNowStrict } from "date-fns";
import { Archive, ClipboardCheck, ExternalLink, Save, Search, WandSparkles } from "lucide-react";
import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import {
  DateRangeFilter,
  presetToSince,
  type DateRangePreset,
} from "@/components/inbox/date-range-filter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useAnalyzeSignalCandidate,
  usePublishSignalCandidates,
  useSignalCandidates,
} from "@/hooks/useSignalCandidates";
import { useUpdateInboxItem, useUpdateInboxItems } from "@/hooks/useUpdateInboxItem";
import type { SignalCandidate, SignalCandidateItem } from "@/lib/api";

const DEFAULT_TYPES = ["trade_alert", "buy", "sell", "watchlist", "vendor_model_portfolio"];
const DEFAULT_CORRELATION_WINDOW = "7d";

export default function SignalCandidatesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const symbolParam = searchParams.get("symbol")?.trim().toUpperCase() ?? "";
  const [preset, setPreset] = useState<DateRangePreset>("today");
  const [timeBasis, setTimeBasis] = useState<"captured" | "source">("captured");
  const [symbolDraft, setSymbolDraft] = useState(symbolParam);
  const [symbol, setSymbol] = useState<string | undefined>(symbolParam || undefined);
  const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
  const since = useMemo(() => presetToSince(preset), [preset]);
  const candidatesQ = useSignalCandidates({
    item_types: DEFAULT_TYPES,
    symbol,
    since,
    time_basis: timeBasis,
    include_hidden: false,
    limit: 500,
    correlation_window: DEFAULT_CORRELATION_WINDOW,
  });
  const candidates = candidatesQ.data?.items ?? [];
  const selectedCandidate = candidates.find((candidate) => candidate.candidate_id === selectedCandidateId) ?? null;

  useEffect(() => {
    setSymbolDraft(symbolParam);
    setSymbol(symbolParam || undefined);
    setSelectedCandidateId(null);
  }, [symbolParam]);

  const commitSymbol = () => {
    const cleaned = symbolDraft.trim().toUpperCase();
    setSymbol(cleaned || undefined);
    setSelectedCandidateId(null);
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      if (cleaned) {
        next.set("symbol", cleaned);
      } else {
        next.delete("symbol");
      }
      return next;
    });
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-6 py-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">Signal Candidates</h1>
            <p className="text-xs text-muted-fg">
              Grouped trade candidates from inbox messages.
            </p>
          </div>
          <div className="text-xs text-muted-fg tabular-nums">
            {candidatesQ.isLoading ? "Loading..." : `${candidates.length.toLocaleString()} candidates`}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <DateRangeFilter value={preset} onChange={setPreset} />
          <TimeBasisFilter value={timeBasis} onChange={setTimeBasis} />
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-fg" />
            <input
              type="text"
              placeholder="Symbol"
              value={symbolDraft}
              onChange={(event) => setSymbolDraft(event.currentTarget.value)}
              onBlur={commitSymbol}
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") commitSymbol();
              }}
              className="h-9 w-[130px] rounded-md border border-border bg-transparent pl-8 pr-2 text-sm placeholder:text-muted-fg focus:outline-none focus:ring-1 focus:ring-accent"
            />
          </div>
          {symbol ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSymbol(undefined);
                setSymbolDraft("");
                setSearchParams((params) => {
                  const next = new URLSearchParams(params);
                  next.delete("symbol");
                  return next;
                });
              }}
            >
              Clear {symbol}
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {candidatesQ.isError ? (
          <div className="flex h-40 items-center justify-center text-sm text-red-400">
            {(candidatesQ.error as Error).message}
          </div>
        ) : candidatesQ.isLoading && candidates.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-fg">
            Loading candidates...
          </div>
        ) : candidates.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-sm text-muted-fg">
            No symbols match the current filters.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {candidates.map((candidate) => (
              <CandidateRow
                key={candidate.candidate_id}
                candidate={candidate}
                selected={selectedCandidateId === candidate.candidate_id}
                onOpen={() => setSelectedCandidateId(candidate.candidate_id)}
              />
            ))}
          </div>
        )}
      </div>
      <CandidateDetailSheet
        candidate={selectedCandidate}
        open={selectedCandidate !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedCandidateId(null);
        }}
      />
    </div>
  );
}

function CandidateRow({
  candidate,
  selected,
  onOpen,
}: {
  candidate: SignalCandidate;
  selected: boolean;
  onOpen: () => void;
}) {
  const latestTs = candidate.latest_item_timestamp ?? candidate.latest_captured_at;
  const age = formatDistanceToNowStrict(new Date(latestTs), { addSuffix: true });
  const lines = signalDisplayLines(candidate.effective_dsl_text);
  const quality = candidateQuality(candidate);
  return (
    <article className={selected ? "bg-accent/8" : "bg-transparent hover:bg-muted/15"}>
      <button
        type="button"
        className="grid w-full gap-3 px-5 py-3 text-left md:grid-cols-[120px_1fr_220px]"
        onClick={onOpen}
        aria-haspopup="dialog"
      >
        <div>
          <div className="font-mono text-lg font-semibold text-fg">{candidate.symbol}</div>
          {candidate.underlying_symbols.length > 1 ? (
            <div className="text-xs text-muted-fg">
              {candidate.underlying_symbols.join(", ")}
            </div>
          ) : null}
          <div className="text-xs text-muted-fg">
            {candidate.item_count} related message{candidate.item_count === 1 ? "" : "s"}
          </div>
          <div className="truncate text-[11px] text-muted-fg/70">
            {candidate.vendor_id ?? "unknown vendor"} / {candidate.subscription_id ?? "unknown subscription"}
          </div>
        </div>
        <div className="min-w-0">
          <SignalLinePreview lines={lines} fallback={candidatePreviewFallback(candidate)} />
          <div className="mt-1 flex flex-wrap gap-1.5">
            <Badge variant={quality.ready ? "success" : "outline"} className="text-[10px]">
              {quality.ready ? "ready" : "needs analysis"}
            </Badge>
            {quality.multiMessage ? (
              <Badge variant="secondary" className="text-[10px]">
                multi-message
              </Badge>
            ) : null}
            {candidate.latest_action ? (
              <Badge variant="secondary" className="font-mono text-[10px]">
                {candidate.latest_action}
              </Badge>
            ) : null}
            {candidate.item_types.map((type) => (
              <Badge key={type} variant="outline" className="text-[10px]">
                {type}
              </Badge>
            ))}
            <Badge variant="outline" className="text-[10px]">
              window {candidate.correlation_window}
            </Badge>
            {candidate.source_ids.slice(0, 3).map((source) => (
              <Badge key={source} variant="secondary" className="text-[10px]">
                {source}
              </Badge>
            ))}
            {candidate.source_ids.length > 3 ? (
              <Badge variant="secondary" className="text-[10px]">
                +{candidate.source_ids.length - 3}
              </Badge>
            ) : null}
          </div>
        </div>
        <div className="text-right text-xs text-muted-fg">
          <div>{age}</div>
          <div>{format(new Date(latestTs), "PPp")}</div>
        </div>
      </button>
    </article>
  );
}

function CandidateDetailSheet({
  candidate,
  open,
  onOpenChange,
}: {
  candidate: SignalCandidate | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const latestItem = candidate?.items[0] ?? null;
  const analyzeCandidate = useAnalyzeSignalCandidate();
  const prepareDraft = usePublishSignalCandidates();
  const updateItem = useUpdateInboxItem();
  const updateItems = useUpdateInboxItems();
  const [dslDraft, setDslDraft] = useState("");
  const [draftPreview, setDraftPreview] = useState<string | null>(null);
  const currentDsl = candidate?.effective_dsl_text ?? candidate?.latest_dsl_text ?? "";

  useEffect(() => {
    setDslDraft(currentDsl);
    setDraftPreview(null);
  }, [currentDsl, candidate?.candidate_id]);

  const archiveCandidate = async () => {
    if (!candidate) return;
    const inboxIds = candidate.items.map((item) => item.inbox_id);
    try {
      const result = await updateItems.mutateAsync({
        inbox_item_ids: inboxIds,
        status: "archived",
      });
      toast.success(`Archived ${result.updated_count} related inbox message${result.updated_count === 1 ? "" : "s"}`);
      onOpenChange(false);
    } catch {
      // Hook already reports the API error.
    }
  };

  const analyzeCurrent = async () => {
    if (!candidate) return;
    try {
      await analyzeCandidate.mutateAsync({
        inbox_item_ids: candidate.items.map((item) => item.inbox_id),
      });
    } catch {
      // Hook already reports the API error.
    }
  };

  const saveDsl = async () => {
    if (!latestItem) return;
    try {
      await updateItem.mutateAsync({
        id: latestItem.inbox_id,
        dsl_text: dslDraft,
      });
      toast.success("Saved DSL override");
    } catch {
      // Hook already reports the API error.
    }
  };

  const prepareTradePlanDraft = async () => {
    if (!candidate) return;
    try {
      const payload = await prepareDraft.mutateAsync({
        targets: ["trading-bot"],
        candidates: [
          {
            candidate_id: candidate.candidate_id,
            symbol: candidate.symbol,
            inbox_item_ids: candidate.items.map((item) => item.inbox_id),
            effective_dsl_text: dslDraft.trim() || null,
          },
        ],
      });
      const result = payload.results[0];
      const findings = result?.validation_findings.filter((finding) => finding.severity === "error") ?? [];
      const status = result?.target_available
        ? `Outbox ${result.outbox_status}${findings.length ? ` · ${findings.length} validation issue${findings.length === 1 ? "" : "s"}` : ""}`
        : `Queued · ${result?.error_message ?? "trading-bot unavailable"}`;
      setDraftPreview(status);
    } catch {
      // Hook already reports the API error.
    }
  };

  const dslDirty = dslDraft !== currentDsl;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col overflow-hidden sm:max-w-3xl">
        {candidate ? (
          <>
            <SheetHeader>
              <SheetTitle className="font-mono">{candidate.symbol}</SheetTitle>
              <SheetDescription>
                {candidate.vendor_id ?? "unknown vendor"} / {candidate.subscription_id ?? "unknown subscription"} · {candidate.item_count} related message
                {candidate.item_count === 1 ? "" : "s"}
              </SheetDescription>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {candidateQuality(candidate).labels.map((label) => (
                  <Badge key={label} variant={label === "ready" ? "success" : "outline"} className="text-[10px]">
                    {label}
                  </Badge>
                ))}
              </div>
            </SheetHeader>
            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto pr-1">
              <section>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h3 className="text-[10px] font-medium uppercase tracking-wider text-muted-fg/80">
                    Current signal
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1 px-2 text-xs"
                      onClick={analyzeCurrent}
                      disabled={!candidate.items.length || analyzeCandidate.isPending}
                      title="Analyze all related inbox messages together"
                    >
                      <WandSparkles className="size-3" aria-hidden />
                      Analyze candidate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1 px-2 text-xs"
                      onClick={prepareTradePlanDraft}
                      disabled={!candidate.items.length || !dslDraft.trim() || prepareDraft.isPending}
                      title="Prepare this candidate for trading-bot draft handling"
                    >
                      <ClipboardCheck className="size-3" aria-hidden />
                      Prepare draft
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1 px-2 text-xs"
                      onClick={archiveCandidate}
                      disabled={updateItems.isPending}
                      title="Archive all related inbox messages in this candidate"
                    >
                      <Archive className="size-3" aria-hidden />
                      Archive candidate
                    </Button>
                  </div>
                </div>
                <div className="space-y-3 rounded-md border border-border/60 bg-bg/50 p-3">
                  <SignalLinePreview
                    lines={signalDisplayLines(candidate.effective_dsl_text)}
                    fallback={candidatePreviewFallback(candidate)}
                    expanded
                  />
                  <div>
                    <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-muted-fg/70">
                      Effective DSL
                    </div>
                    <textarea
                      value={dslDraft}
                      onChange={(event) => setDslDraft(event.currentTarget.value)}
                      rows={Math.min(5, Math.max(2, dslDraft.split("\n").length))}
                      className="w-full resize-y rounded border border-border/50 bg-black/15 p-2 font-mono text-xs leading-relaxed text-fg outline-none focus:ring-1 focus:ring-accent"
                      placeholder="Add or correct the operator-approved DSL for this candidate"
                    />
                    <div className="mt-2 flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 px-2 text-xs"
                        onClick={saveDsl}
                        disabled={!latestItem || !dslDirty || updateItem.isPending}
                      >
                        <Save className="size-3" aria-hidden />
                        Save DSL
                      </Button>
                    </div>
                  </div>
                </div>
                {draftPreview ? (
                  <div className="rounded-md border border-border/60 bg-black/20 p-3">
                    <div className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-fg/70">
                      Draft readiness
                    </div>
                    <p className="text-xs leading-relaxed text-muted-fg">{draftPreview}</p>
                  </div>
                ) : null}
              </section>
              <section>
                <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-fg/80">
                  Related inbox messages
                </h3>
                <div className="space-y-2">
                  {candidate.items.map((item) => (
                    <RelatedInboxMessage key={item.inbox_id} item={item} />
                  ))}
                </div>
              </section>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function RelatedInboxMessage({ item }: { item: SignalCandidateItem }) {
  const timestamp = item.item_timestamp ?? item.captured_at;
  return (
    <div className="rounded-md border border-border/50 bg-bg/45 p-3 text-xs">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-fg">
            {item.vendor_id ?? "unknown vendor"} / {item.source_id ?? "unknown source"}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-muted-fg">
            <Badge variant="outline" className="text-[10px]">
              {item.item_type ?? "unknown"}
            </Badge>
            <Badge variant="secondary" className="text-[10px]">
              {roleLabel(item.contribution_role)}
            </Badge>
            <span>{item.status}</span>
            <span>{format(new Date(timestamp), "PPp")}</span>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Link
            to={`/inbox?item=${encodeURIComponent(item.inbox_id)}`}
            className="rounded px-2 py-1 text-accent hover:bg-muted"
            title="Open this inbox message"
          >
            Inbox
          </Link>
          {item.page_url ? (
            <a
              href={item.page_url}
              target="_blank"
              rel="noreferrer"
              className="rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
              title="Open original item"
            >
              <ExternalLink className="size-4" aria-hidden />
            </a>
          ) : null}
        </div>
      </div>
      <div className="mt-3">
        <SignalLinePreview
          lines={signalDisplayLines(item.dsl_text)}
          fallback={item.preview_text ?? "No content"}
          expanded
        />
      </div>
      {item.preview_text && item.dsl_text ? (
        <div className="mt-3 rounded border border-border/40 bg-black/15 p-2 text-muted-fg">
          {item.preview_text}
        </div>
      ) : null}
    </div>
  );
}

function roleLabel(role: string): string {
  return role.replace(/_/g, " ");
}

function SignalLinePreview({
  lines,
  fallback,
  expanded = false,
}: {
  lines: SignalDisplayLine[];
  fallback: string;
  expanded?: boolean;
}) {
  if (lines.length === 0) {
    return <div className="text-sm text-muted-fg">{fallback}</div>;
  }
  const visible = expanded ? lines : lines.slice(0, 3);
  return (
    <div className="space-y-1.5">
      {visible.map((line, index) => (
        <div key={`${line.title}-${index}`} className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="font-mono text-[10px] uppercase">
              {line.action}
            </Badge>
            <span className="font-mono text-sm text-fg">{line.symbol}</span>
            {line.levels.length > 0 ? (
              <span className="truncate text-xs text-muted-fg">
                {line.levels.join(" · ")}
              </span>
            ) : null}
          </div>
          {line.legs.length > 0 ? (
            <div className="mt-1 flex flex-wrap gap-1.5">
              {line.legs.map((leg) => (
                <span
                  key={leg}
                  className="rounded border border-border/50 bg-black/15 px-2 py-0.5 font-mono text-[11px] text-muted-fg"
                >
                  {leg}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      ))}
      {!expanded && lines.length > visible.length ? (
        <div className="text-xs text-muted-fg">
          +{lines.length - visible.length} more
        </div>
      ) : null}
    </div>
  );
}

interface SignalDisplayLine {
  title: string;
  action: string;
  symbol: string;
  legs: string[];
  levels: string[];
}

function signalDisplayLines(dslText: string | null): SignalDisplayLine[] {
  if (!dslText) return [];
  return dslText
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map(signalDisplayLine)
    .filter((line): line is SignalDisplayLine => line !== null);
}

function signalDisplayLine(line: string): SignalDisplayLine | null {
  const normalized = line.replace(/\s+·\s+/g, " ");
  const match = /^(?:SIGNAL\s+)?(?<action>BUY|SELL|EXIT|WATCH|WATCHLIST|SHORT|COVER|TRIM|ADD)\s+(?<symbol>[A-Z0-9.]+)\s*(?<rest>.*)$/i.exec(normalized);
  if (!match?.groups) return null;
  const action = displayAction(match.groups.action);
  const symbol = match.groups.symbol.toUpperCase();
  const rest = match.groups.rest ?? "";
  const legs = paramValue(rest, "legs")?.split("|").filter(Boolean) ?? [];
  const levels = signalLevelLabels(rest);
  return {
    title: `${action} ${symbol}`,
    action,
    symbol,
    legs,
    levels,
  };
}

function candidateQuality(candidate: SignalCandidate): {
  ready: boolean;
  multiMessage: boolean;
  labels: string[];
} {
  const ready = Boolean(candidate.effective_dsl_text?.trim());
  const multiMessage = candidate.item_count > 1;
  return {
    ready,
    multiMessage,
    labels: [
      ready ? "ready" : "needs analysis",
      multiMessage ? "multi-message" : "single message",
      `${candidate.item_count} inbox`,
    ],
  };
}

function candidatePreviewFallback(candidate: SignalCandidate): string {
  const latest = candidate.items[0];
  if (latest?.preview_text) return latest.preview_text;
  if (candidate.latest_action) return `${candidate.latest_action} ${candidate.symbol}`;
  return "No structured signal yet";
}

function signalLevelLabels(text: string): string[] {
  return [
    ...legacyLevelLabels(text),
    ...tokenLevelLabels(text, "ENTRY"),
    ...tokenLevelLabels(text, "SL"),
    ...tokenLevelLabels(text, "TP"),
    ...tokenLevelLabels(text, "CONF"),
  ];
}

function legacyLevelLabels(text: string): string[] {
  return [
    ["entry", "ENTRY"],
    ["stop", "SL"],
    ["target", "TP"],
    ["confidence", "CONF"],
  ]
    .map(([key, label]) => {
      const value = paramValue(text, key);
      return value ? `${label} ${value}` : null;
    })
    .filter((value): value is string => value !== null);
}

function tokenLevelLabels(text: string, token: string): string[] {
  const labels: string[] = [];
  const pattern = new RegExp(`\\b(${token}\\d*)\\s+([^\\s]+)`, "gi");
  for (const match of text.matchAll(pattern)) {
    labels.push(`${match[1].toUpperCase()} ${match[2]}`);
  }
  return labels;
}

function displayAction(action: string): string {
  const upper = action.toUpperCase();
  if (upper === "WATCHLIST") return "WATCH";
  return upper;
}

function paramValue(text: string, key: string): string | null {
  const keys = ["legs", "entry", "stop", "target", "confidence"];
  const nextKey = keys.filter((name) => name !== key).join("|");
  const match = new RegExp(`(?:^|\\s)${key}=(.*?)(?=\\s(?:${nextKey})=|$)`).exec(text);
  return match?.[1]?.trim() ?? null;
}

function TimeBasisFilter({
  value,
  onChange,
}: {
  value: "captured" | "source";
  onChange: (value: "captured" | "source") => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Date mode"
      className="inline-flex h-9 items-center rounded-md border border-border bg-transparent p-0.5 text-sm"
    >
      {[
        { value: "captured" as const, label: "Captured" },
        { value: "source" as const, label: "Source" },
      ].map((option) => (
        <button
          key={option.value}
          role="radio"
          aria-checked={option.value === value}
          type="button"
          onClick={() => onChange(option.value)}
          className={
            option.value === value
              ? "h-full rounded bg-accent/20 px-3 text-xs font-medium text-accent ring-1 ring-accent/40"
              : "h-full rounded px-3 text-xs font-medium text-muted-fg hover:bg-muted/60 hover:text-fg"
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
